/**
 * Baymo preorder by variant (PDP).
 *
 * The product page carries a JSON with the release dates (snippets/baymo-preorder-data):
 * the variant's own custom.fecha_preorder wins, otherwise the product's applies.
 * A variant is in preorder while its date is later than today (visitor's clock).
 *
 * Checked on page load and on every variant change (Horizon `variant:update`),
 * and applied to the product section:
 * - "Aviso preventa" (.baymo-preorder-notice): shown with that variant's date, or hidden
 * - Stock status (.baymo-stock-status): hidden while in preorder
 * - Buy button and sticky add-to-cart: "Preventa" / "Añadir al carrito" (only when the
 *   variant can be bought; sold out / unavailable labels are left to the theme)
 */
import { ThemeEvents } from '@theme/events';

const DATA_SELECTOR = 'script[data-baymo-preorder]';

/** @param {Date} date */
const isoDay = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/** @param {{type: string, value: string|number} | null | undefined} entry */
function isFuture(entry) {
  if (!entry) return false;
  if (entry.type === 'date_time') return Date.now() < Number(entry.value) * 1000;
  return isoDay(new Date()) < String(entry.value);
}

/** dd/mm/yyyy, same format as the Liquid notice */
function formatDate(entry) {
  if (entry.type === 'date_time') {
    const d = new Date(Number(entry.value) * 1000);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }
  const [year, month, day] = String(entry.value).split('-');
  return `${day}/${month}/${year}`;
}

/** @param {HTMLElement} scope */
function readData(scope) {
  const script = scope.querySelector(DATA_SELECTOR);
  if (!script) return null;
  try {
    return JSON.parse(script.textContent || '{}');
  } catch {
    return null;
  }
}

/**
 * Variant currently selected in the main product form. The section also holds other
 * product forms (e.g. "Completa tu look" cards), so the buy buttons block is preferred.
 */
function currentVariantId(scope) {
  const input =
    scope.querySelector('.baymo-buy-buttons input[name="id"]') ||
    scope.querySelector('.buy-buttons-block input[name="id"]');
  return (
    input?.value ||
    scope.querySelector('sticky-add-to-cart')?.dataset.currentVariantId ||
    new URL(window.location.href).searchParams.get('variant') ||
    ''
  );
}

/**
 * @param {HTMLElement} scope - The product section
 * @param {string} variantId
 * @param {boolean} [available] - From the variant update event (undefined on load)
 */
function applyPreorder(scope, variantId, available) {
  const data = readData(scope);
  if (!data) return;

  const entry = (variantId && data.variants?.[variantId]) || data.product;
  const inPreorder = isFuture(entry);
  scope.dataset.baymoPreorder = String(inPreorder);

  for (const notice of scope.querySelectorAll('.baymo-preorder-notice')) {
    notice.hidden = !inPreorder;
    const message = notice.querySelector('.baymo-preorder-notice__message');
    if (inPreorder && message && entry) {
      const prefix = notice.dataset.prefix;
      message.textContent = `${prefix ? `${prefix}: ` : ''}${formatDate(entry)}`;
    }
  }

  for (const stock of scope.querySelectorAll('.baymo-stock-status')) {
    stock.hidden = inPreorder;
  }

  // Button labels only for buyable variants; sold out / unavailable keep the theme text
  for (const buyButtons of scope.querySelectorAll('.baymo-buy-buttons')) {
    const button = buyButtons.querySelector('[ref="addToCartButton"]');
    if (!button || available === false || button.disabled) continue;
    const label = buyButtons.querySelector('.add-to-cart-text__content > span > span');
    const text = inPreorder ? buyButtons.dataset.labelPreorder : buyButtons.dataset.labelAdd;
    if (label && text) label.textContent = text;
  }

  const sticky = scope.querySelector('sticky-add-to-cart');
  if (sticky && available !== false && sticky.dataset.variantAvailable !== 'false') {
    const label = sticky.querySelector('.add-to-cart-text__content > span:not(.quantity-display)');
    const text = inPreorder ? data.labels?.preorder : data.labels?.add_to_cart;
    if (label && text) label.textContent = text;
  }
}

function initAll() {
  for (const script of document.querySelectorAll(DATA_SELECTOR)) {
    const scope = script.closest('.shopify-section') || document.body;
    applyPreorder(scope, currentVariantId(scope));
  }
}

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const scope = /** @type {HTMLElement} */ (event.target)?.closest?.('.shopify-section');
  if (!scope || !scope.querySelector(DATA_SELECTOR)) return;
  const variant = event.detail?.resource;
  // Run after Horizon has morphed the buy button / sticky bar for the new variant
  // (setTimeout instead of requestAnimationFrame: it also runs in background tabs)
  setTimeout(() => applyPreorder(scope, variant?.id ? String(variant.id) : currentVariantId(scope), variant?.available), 0);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}
